import { useId, useState } from 'react';
import { format } from 'date-fns-tz';

import { initials, PersonAvatar } from 'components/person-avatar';
import { ConfirmacaoDaAcao } from 'components/task-item/modal-actions/moldura-da-acao';
import { TaskItemProps, useTaskItemEcoa } from 'components/task-item-ecoa/useTaskItemEcoa';
import AgendaDaRealizacao, { HUMANOGRAMA, PilulaDaEtapa } from 'pages/RealizacaoDestaque/components/AgendaDaRealizacao';
import { rostoDoAutor, rostoDoParticipante } from 'pages/RealizacaoDestaque/identidade-do-participante';
import { FOCO_VISIVEL } from 'utils/foco';

import { eixoDaAcao, FaseEcoa } from '../acoes-do-semestre';
import { etapasDaAcaoEcoa } from '../andamento-da-acao';
import { dataCurta, FALHA_NA_ACAO, ModaisDaAcaoEcoa } from './comum-dos-cartoes';
import DetalhesDaAcaoEcoa from './DetalhesDaAcaoEcoa';
import ExclusaoDoIntegrante from './ExclusaoDoIntegrante';

export const AVISO_DE_EXCLUSAO = 'Sua Ação ECOA foi excluída.';

const MAXIMO_DE_INICIAIS = 5;

const ACAO_DE_TEXTO = `cursor-pointer border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] hover:underline ${FOCO_VISIVEL}`;


function dataDoFeedback(ms: number | undefined): string {
  return typeof ms === 'number' ? format(new Date(ms), 'dd/MM/yyyy') : '';
}

/** Integrantes do resumo: nome de guerra de `target` quando a leitura o traz, senão as chaves de `team`. */
function integrantesDe(data: any): { chave: string; nome: string; rosto: ReturnType<typeof rostoDoParticipante> }[] {
  if (Array.isArray(data.target) && data.target.length) {
    return data.target.map((pessoa: any) => ({
      chave: pessoa._id,
      nome: pessoa.extra?.nome_guerra || pessoa.name || pessoa._id,
      rosto: rostoDoParticipante(pessoa),
    }));
  }
  return Array.isArray(data.team) ? data.team.map((membro: any) => ({ chave: membro._id, nome: membro._id, rosto: {} })) : [];
}

export interface CartaoDaMinhaAcaoProps {
  data: any;
  /** Fase mensal em que a ação foi cadastrada: o `cycle` que a página antiga passava ao `TaskItemEcoa`. */
  fase: FaseEcoa | undefined;
  rotuloDaFase: string;
  /** `userInfo` do contexto, como na página antiga. */
  playerInfo: any;
  /** `acaoEditavel` (regra `taskEditable` de hoje). */
  editavel: boolean;
  /** Exclusão confirmada: a página anuncia o aviso e relê o ciclo (ECN-12, ECN-63). */
  onConcluida: (aviso: string) => void;
  /** Depois de uma ação dos modais do "Ver mais": a página relê o ciclo. */
  onRecarregar: () => void;
}

/** Cartão da Ação ECOA própria (ECN-08…ECN-12, ECN-16) sobre as regras do `TaskItemEcoa` (`useTaskItemEcoa`). */
export default function CartaoDaMinhaAcao({ data, fase, rotuloDaFase, playerInfo, editavel, onConcluida, onRecarregar }: CartaoDaMinhaAcaoProps) {
  const modelo = useTaskItemEcoa({
    data,
    cycle: fase as TaskItemProps['cycle'],
    playerInfo,
    editable: editavel,
    onReload: onRecarregar,
    ecoa: true,
  });
  const etapas = etapasDaAcaoEcoa(modelo);
  const etapaAtual = etapas.find(etapa => etapa.atual);
  const integrantes = integrantesDe(data);
  const feedbacks: any[] = Array.isArray(data.feedback) ? data.feedback : [];
  const responsavel = data.original_manager_detail?.nome_guerra ? modelo.capitalizeWords(data.original_manager_detail.nome_guerra) : undefined;

  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const tituloId = useId();
  const detalhesId = useId();

  async function excluir() {
    if (enviando) return;
    setEnviando(true);
    setFalhou(false);
    try {
      await modelo.handleDelete(data._id);
      setConfirmando(false);
      onConcluida(AVISO_DE_EXCLUSAO);
    } catch {
      setConfirmando(false);
      setFalhou(true);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <article aria-labelledby={tituloId} className="rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
      <div className="flex flex-wrap items-center gap-3 border-b border-surface-200 pb-4">
        <PersonAvatar
          {...rostoDoAutor(data)}
          initials={initials(data.owner_name_guerra || data.owner_name || '')}
          size={44}
          initialsClassName="bg-brand-green font-display text-xs font-bold text-brand-navy"
        />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[13px] text-content">
            <a
              href={`${HUMANOGRAMA}${data.owner}`}
              target="_blank"
              rel="noreferrer"
              className={`font-bold text-link hover:text-link-hover ${FOCO_VISIVEL}`}
            >
              {`${(data.owner_name_guerra || data.owner_name || '').split(' ')[0]} (${data.owner})`}
            </a>{' '}
            cadastrou · <span className="text-content-soft">{rotuloDaFase}</span>
          </p>
          <p className="m-0 mt-1 text-[11.5px] text-content-soft">
            {`Data do cadastro: ${dataCurta(data.created)}`}
            {!!data.updated && ` · Última atualização: ${dataCurta(data.updated)}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {modelo.showDeleteButton && (
            // SPEC_DEVIATION: `text-urgent` no lugar do `brand-pink` do design (2,9:1 no branco).
            <button type="button" aria-haspopup="dialog" onClick={() => setConfirmando(true)} className={`${ACAO_DE_TEXTO} text-urgent`}>
              Excluir ação
            </button>
          )}
          {modelo.showEditButton && (
            <button type="button" onClick={modelo.handleEdit} className={`${ACAO_DE_TEXTO} text-brand-blue`}>
              Editar ação
            </button>
          )}
        </div>
      </div>

      <div className="mb-3 mt-[18px] flex flex-wrap items-center gap-2.5">
        <p className="m-0 font-display text-[13px] font-bold text-brand-blue-dark">Acompanhe o andamento da ação</p>
        <PilulaDaEtapa status={etapaAtual?.status} />
      </div>
      <AgendaDaRealizacao etapas={etapas} />

      <dl className="m-0 mt-5 flex flex-wrap gap-x-[26px] gap-y-4 border-t border-surface-200 pt-4">
        <div className="min-w-0">
          <dt className="mb-1 text-[11px] text-content-soft">Nome da ação</dt>
          <dd className="m-0">
            {/* `leading-[normal]`: o rsuite.min.css global põe `h3 { line-height: 42px }` e o título desceria. */}
            <h3 id={tituloId} className="m-0 leading-[normal] text-[13px] font-bold text-content">
              {data.title || '---'}
            </h3>
          </dd>
        </div>
        <div>
          <dt className="mb-1 text-[11px] text-content-soft">Eixo temático</dt>
          <dd className="m-0 text-[13px] font-bold text-content">{eixoDaAcao(data)}</dd>
        </div>
        {!!responsavel && (
          <div>
            <dt className="mb-1 text-[11px] text-content-soft">Responsável pela apreciação</dt>
            <dd className="m-0 text-[13px] font-bold text-content">{responsavel}</dd>
          </div>
        )}
        <div>
          <dt className="mb-1 text-[11px] text-content-soft">Pontos recebidos</dt>
          <dd className="m-0 font-display text-base font-bold text-brand-blue">{modelo.points}</dd>
        </div>
        {integrantes.length > 0 && (
          <div>
            <dt className="mb-1 text-[11px] text-content-soft">{`Participantes (${integrantes.length})`}</dt>
            <dd className="m-0 flex items-center">
              {/* As iniciais são decorativas; o nome de todos vai por extenso ao leitor de tela (WCAG 1.1.1). */}
              <span className="sr-only">{integrantes.map(pessoa => pessoa.nome).join(', ')}</span>
              {integrantes.slice(0, MAXIMO_DE_INICIAIS).map(pessoa => (
                <span
                  key={pessoa.chave}
                  aria-hidden="true"
                  title={pessoa.nome}
                  data-integrante-resumo={pessoa.chave}
                  className="-mr-1.5 block rounded-full border-2 border-brand-white"
                >
                  <PersonAvatar
                    {...pessoa.rosto}
                    initials={initials(pessoa.nome)}
                    size={22}
                    initialsClassName="bg-brand-green font-display text-[9px] font-bold text-brand-navy"
                  />
                </span>
              ))}
              {integrantes.length > MAXIMO_DE_INICIAIS && (
                <span aria-hidden="true" className="ml-3 text-[11px] font-bold text-content-soft">
                  {`+${integrantes.length - MAXIMO_DE_INICIAIS}`}
                </span>
              )}
            </dd>
          </div>
        )}
      </dl>

      {feedbacks.length > 0 && (
        <section aria-label="Feedback recebido" className="mt-4 rounded-jj-md bg-brand-green-light px-4 py-3">
          <h4 className="m-0 mb-1.5 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-navy">Feedback recebido</h4>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {feedbacks.map((feedback, indice) => (
              <li key={feedback._id ?? indice} data-feedback={feedback._id} className="text-xs leading-[1.6] text-brand-navy">
                <p className="m-0 font-bold">
                  {[feedback.evaluator_nome_guerra, dataDoFeedback(feedback.created)].filter(Boolean).join(' · ')}
                </p>
                {/* Mesmo HTML que o `TaskItemEcoa` renderiza para a mensagem do feedback. */}
                <p className="m-0 italic" dangerouslySetInnerHTML={{ __html: feedback.message }} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <div id={detalhesId}>
        {modelo.oppened && (
          <DetalhesDaAcaoEcoa
            data={data}
            getFormattedDate={modelo.getFormattedDate}
            responsavel={responsavel}
            acoesDoIntegrante={pessoa => <ExclusaoDoIntegrante pessoa={pessoa} modelo={modelo} />}
          />
        )}
      </div>

      <button
        type="button"
        aria-expanded={modelo.oppened}
        aria-controls={detalhesId}
        onClick={() => modelo.setOppened(!modelo.oppened)}
        className={`mx-auto mt-4 flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.04em] text-content-soft ${FOCO_VISIVEL}`}
      >
        <span aria-hidden="true" className="text-[13px] leading-none">
          {modelo.oppened ? '−' : '+'}
        </span>
        {`Ver ${modelo.oppened ? 'menos' : 'mais'} informações`}
      </button>

      {/* Sempre montada: o leitor de tela anuncia a falha quando o texto entra (ECN-16, ECN-63). */}
      <p role="status" className="m-0 mt-2 text-center text-xs font-bold text-urgent">
        {falhou ? FALHA_NA_ACAO : ''}
      </p>

      <ConfirmacaoDaAcao
        isOpen={confirmando}
        title="Excluir sua Ação ECOA?"
        onClose={() => !enviando && setConfirmando(false)}
        backLabel="Manter"
        backDisabled={enviando}
        closeDisabled={enviando}
        confirmLabel="Excluir ação"
        confirmDisabled={enviando}
        onConfirm={excluir}
      >
        Ao excluir, a ação sai da fase e os integrantes incluídos perdem a indicação. Você pode cadastrar outra enquanto o
        cadastramento estiver aberto.
      </ConfirmacaoDaAcao>

      <ModaisDaAcaoEcoa modelo={modelo} />
    </article>
  );
}
